import React, { useContext } from 'react'
import styled, { DefaultTheme } from 'styled-components'
import Card from '@jarvis.oddle/core/components/Card'
import {
  borderRadiusSizes,
  colors,
  marginSizes,
  padding,
  paddingSizes
} from '@jarvis.oddle/core/theme/default'
import StoreOrderSummary from 'src/components/molecules/StoreOrderSummary'
import OrderItemsSummary from 'src/components/molecules/OrderItemsSummary'
import { H4, P } from '@jarvis.oddle/core/components/Typo'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import Div from '@jarvis.oddle/core/components/Div'
import Divider from '@jarvis.oddle/core/components/Divider'
import { formatDate } from 'src/utils/date'
import { useRouter } from 'next/router'

interface OrderSummaryContainerProps {
  theme?: DefaultTheme
  error?: string[]
}

const OrderSummaryContainer = styled(Card).attrs(() => ({
  borderRadius: borderRadiusSizes.small
}))`
  width: 100%;
  background-color: ${(
    props = {
      theme: {
        colorContainerColor: colors.container
      }
    } as OrderSummaryContainerProps
  ) => props.theme.colorContainerColor};
  padding: ${padding[paddingSizes.medium]};
  position: sticky;
  top: ${padding[paddingSizes.medium]};
  ${(props: OrderSummaryContainerProps) =>
    props.error ? 'height: 35vh;' : ''};
`

interface OrderSummaryProps {
  sticky?: boolean
  voucherSummary?: {
    voucherTitle: string
    redeemedAmount: number
  }
  error?: string | {
    errorCode: string
  }[]
  storeOrder: {
    data: Oms.V4.StoreOrder
  }
  orderItems: Oms.V4.OrderItem[]
  promotionalOrderItems: Oms.V4.OrderItem[]
}

const OrderSummary = ({
  sticky = false,
  voucherSummary,
  error,
  storeOrder,
  orderItems,
  promotionalOrderItems
}: OrderSummaryProps): JSX.Element => {
  const { t } = useContext(TranslationContext)
  const Container = sticky ? OrderSummaryContainer : React.Fragment
  const router = useRouter()

  return (
    <Container error={error}>
      {!error && (
        <React.Fragment>
          <Div marginTop={marginSizes.medium} marginBottom={marginSizes.medium}>
            <P normalised marginBottom={marginSizes.small}>
              {t('Ordered On')}
            </P>
            <H4 bold normalised>
              {formatDate({
                t,
                date: storeOrder.data?.orderDate,
                locale: router.query.locale as string,
                options: { year: 'numeric', month: 'long', day: 'numeric' }
              })}
            </H4>
          </Div>
          <Divider />
          <OrderItemsSummary
            orderItems={orderItems}
            promotionalOrderItems={promotionalOrderItems}
          />
          <StoreOrderSummary
            shouldShowTotal
            shouldShowVoucherSummary
            shouldShowSurchargeWarning
            voucherSummary={voucherSummary}
            storeOrder={storeOrder.data || {} as Oms.V4.StoreOrder}
          />
        </React.Fragment>
      )}
    </Container>
  )
}

export default OrderSummary
